"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const big_js_1 = require("big.js");
const moneyFormatters_1 = require("./moneyFormatters");
const times_1 = __importDefault(require("./times"));
function Money(cents) {
    if (cents === 0 || cents === "0" || cents === "") {
        return ZERO;
    }
    return new Money_(cents);
}
class Money_ {
    constructor(cents) {
        try {
            this.cents = big_js_1.Big(cents);
        }
        catch (err) {
            console.error(`Cannot convert '${cents}' to Money: ${err}`);
            this.cents = big_js_1.Big(0);
        }
    }
    add(m) {
        return new Money_(this.cents.plus(m.cents));
    }
    subtract(m) {
        return new Money_(this.cents.minus(m.cents));
    }
    multiply(v) {
        if (v === 0 || isNaN(v))
            return ZERO;
        return new Money_(this.cents.times(v));
    }
    divide(v) {
        if (v === 0 || isNaN(v))
            return ZERO;
        return new Money_(this.cents.div(v));
    }
    negate() {
        return new Money_(this.cents.times(-1));
    }
    split(numSplits) {
        // Guard against NaN, which is a type of number
        if (numSplits === 0 || isNaN(numSplits))
            return [];
        // Splitting a zero value just returns multiple zeroes
        if (this.isZero())
            return times_1.default(numSplits, () => this);
        const eachVal = this.cents.div(numSplits).round(0, 1 /* RoundHalfUp */);
        const roundedSplitSum = eachVal.times(numSplits);
        const diff = this.cents.minus(roundedSplitSum);
        const diffSize = parseInt(diff.abs().toString(), 10);
        return times_1.default(numSplits, n => {
            let val = eachVal;
            if (n + 1 <= diffSize) {
                val = eachVal.plus(diff.cmp(0));
            }
            return new Money_(val);
        });
    }
    /**
     * Compares if two Money values are equal.
     * Does not compare sub-cent values.
     * @return {boolean} If the given value is an equal number of cents
     */
    equals(m) {
        if (m === this)
            return true;
        if (!(m instanceof Money_))
            return false;
        return this.roundToInt().eq(m.roundToInt());
    }
    lte(m) {
        if (m === this)
            return true;
        if (!(m instanceof Money_))
            return false;
        return this.roundToInt().lte(m.roundToInt());
    }
    lt(m) {
        if (m === this)
            return false;
        if (!(m instanceof Money_))
            return false;
        return this.roundToInt().lt(m.roundToInt());
    }
    round() {
        return new Money_(this.roundToInt().toString());
    }
    roundToInt() {
        if (isInteger(this.cents)) {
            return this.cents; // optimization when cents is already rounded
        }
        return this.cents.round(0, 1 /* RoundHalfUp */);
    }
    format() {
        return moneyFormatters_1.formatMoney(this.toDollars());
    }
    toDollars() {
        return Number(this.cents.div(100).round(2, 1 /* RoundHalfUp */));
    }
    toString() {
        return this.cents.toString();
    }
    valueOf() {
        const val = parseInt(this.roundToInt().toString(), 10);
        if (isNaN(val)) {
            console.error("Cannot convert 'NaN' to Money");
            return 0;
        }
        else {
            return val;
        }
    }
    concat(m) {
        return this.add(m);
    }
    percentageOf(m) {
        if (m.isZero())
            return 0;
        return Number(this.cents.div(m.cents).round(4));
    }
    static empty() {
        return ZERO;
    }
    static isMoney(m) {
        return m instanceof Money_;
    }
    /**
     * Compares the current amount to 0.
     * Any value closer to 0 than 0.5 cents is equal to 0.
     * @return {boolean} If this value is currently zero
     */
    isZero() {
        return this.equals(ZERO);
    }
    static fromDollars(value) {
        if (typeof value === "string") {
            value = parseFloat(value);
        }
        // Use Math.round on (value * 100) because in JavaScript:
        // 1.1 * 100 = 110.00000000000001
        // 35.41 * 100 = 3540.9999999999995
        return value === 0 ? ZERO : new Money_(Math.round(value * 100));
    }
}
function isInteger(n) {
    // The exponent is positive and one less than the size of the coefficient
    return n.e + 1 == n.c.length;
}
const ZERO = new Money_(0);
(function (Money) {
    Money.empty = Money_.empty;
    Money.zero = Money_.empty;
    Money.isMoney = Money_.isMoney;
    Money.fromDollars = Money_.fromDollars;
})(Money || (Money = {}));
exports.default = Money;
